export type AnswerStatus = 'confirmed' | 'inferred' | 'unknown';

export type AnswerMeta = {
  status: AnswerStatus;
  reason?: string;
};

export type YesNoUnknown = 'yes' | 'no' | 'unknown';
export type SystemType = 'combi' | 'system' | 'regular' | 'unknown';
export type BathroomsCount = 1 | 2 | 3;
export type OccupancyBand = '1-2' | '3-4' | '5+';
export type UsageLevel = 'low' | 'medium' | 'high';
export type PeakPattern = 'sequential' | 'often_simultaneous' | 'unknown';
export type SludgeHistory = 'recently_cleaned_inhibited' | 'all_rads_even' | 'suspected' | 'confirmed';
export type ServiceRecency = 'within_12m' | 'over_12m_or_unknown';
export type CleanRecency = 'cleaned_recently' | 'not_recently_or_unknown';
export type ControlsPreference = 'simple' | 'typical' | 'advanced';
export type PropertyType = 'detached' | 'semi-detached' | 'terrace' | 'flat' | 'bungalow';
export type AgeBand = 'pre-1930' | '1930-1979' | '1980-2000' | '2001-2010' | '2011+';
export type EmitterType = 'radiators' | 'ufh' | 'mixed' | 'unknown';
export type LowTempDesign = 'yes' | 'no' | 'unknown';
export type ChCircuitType = 'open_vented' | 'sealed_pressurised' | 'unknown';
export type DhwStorageType = 'vented_cylinder' | 'unvented_cylinder' | 'thermal_store' | 'unknown';
export type DhwDeliveryMode = 'open_vented' | 'open_vented_pumped' | 'mains' | 'unknown';
export type DhwCirculationMode = 'none' | 'keep_hot' | 'timed_bronze_return';
export type ColdMainType =
  | 'mdpe_25_blue'
  | 'mdpe_20_blue'
  | 'lead'
  | 'copper_15'
  | 'copper_22'
  | 'mixed_necked_down'
  | 'unknown';
export type PipeworkPrimarySize = '15' | '22' | '28' | 'unknown';
export type InsulationLevel = 'poor' | 'average' | 'good' | 'unknown';

export type ExpertAnswers = {
  heatLossKw: number | null;
  heatLossUnknown: boolean;
  propertyType: PropertyType | null;
  ageBand: AgeBand | null;
  insulationLevel: InsulationLevel | null;
  emitterType: EmitterType | null;
  lowTempDesign: LowTempDesign | null;
  systemType: SystemType | null;
  mainsFlowLpm: number | null;
  mainsStaticPressureBar: number | null;
  cylinderPresent: YesNoUnknown;
  chCircuitType: ChCircuitType | null;
  dhwStorageType: DhwStorageType | null;
  dhwDeliveryMode: DhwDeliveryMode | null;
  hasSecondaryReturnLoop: YesNoUnknown;
  dhwCirculationMode: DhwCirculationMode;
  coldMainType: ColdMainType | null;
  actualAnnualEnergyKwh: number | null;
  staticPressureBar: number | null;
  staticFlowLpm: number | null;
  dynamicFlowLpm: number | null;
  dynamicPressureBar: number | null;
  outsideSpace: YesNoUnknown;
  pipeworkMicrobore: YesNoUnknown;
  pipeworkPrimarySize: PipeworkPrimarySize;
  underfloorHeating: YesNoUnknown;
  occupancyBand: OccupancyBand | null;
  bathrooms: BathroomsCount | null;
  simultaneousUse: UsageLevel | null;
  peakPattern: PeakPattern | null;
  bathPresent: YesNoUnknown;
  filterPresent: YesNoUnknown;
  sludgeHistory: SludgeHistory;
  serviceRecency: ServiceRecency;
  cleanRecency: CleanRecency;
  controlsPreference: ControlsPreference | null;
};

export type ExpertAnswerField = keyof ExpertAnswers;

export type ExpertAnswerMeta = Record<ExpertAnswerField, AnswerMeta>;

export const defaultAnswers: ExpertAnswers = {
  heatLossKw: null,
  heatLossUnknown: true,
  propertyType: null,
  ageBand: null,
  insulationLevel: null,
  emitterType: null,
  lowTempDesign: null,
  systemType: null,
  mainsFlowLpm: null,
  mainsStaticPressureBar: null,
  cylinderPresent: 'unknown',
  chCircuitType: null,
  dhwStorageType: null,
  dhwDeliveryMode: null,
  hasSecondaryReturnLoop: 'unknown',
  dhwCirculationMode: 'none',
  coldMainType: null,
  actualAnnualEnergyKwh: null,
  staticPressureBar: null,
  staticFlowLpm: null,
  dynamicFlowLpm: null,
  dynamicPressureBar: null,
  outsideSpace: 'unknown',
  pipeworkMicrobore: 'unknown',
  pipeworkPrimarySize: 'unknown',
  underfloorHeating: 'unknown',
  occupancyBand: null,
  bathrooms: null,
  simultaneousUse: null,
  peakPattern: null,
  bathPresent: 'unknown',
  filterPresent: 'unknown',
  sludgeHistory: 'all_rads_even',
  serviceRecency: 'over_12m_or_unknown',
  cleanRecency: 'not_recently_or_unknown',
  controlsPreference: null,
};

export const defaultMeta: ExpertAnswerMeta = Object.keys(defaultAnswers).reduce((acc, key) => {
  acc[key as ExpertAnswerField] = { status: 'unknown' };
  return acc;
}, {} as ExpertAnswerMeta);
